"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ItemListClient = void 0;
const commerce_core_1 = require("@broadleaf/commerce-core");
/**
 * Client used for Operations for management of item lists.
 */
class ItemListClient extends commerce_core_1.AbstractClient {
    constructor(options) {
        super({ ...options, basePath: options.basePath || '/api/cart-operations' });
    }
    /**
     * Retrieves a page of item lists.
     *
     * @param cq Used to provide an RSQL query string for filtering a list of items.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return A page of Item Lists
     */
    async listItemLists(cq, options) {
        const config = {
            method: 'get',
            url: '/item-list-ops',
            params: { cq }
        };
        this.attachNumberedPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Create Item List
     *
     * @param itemList A simple collection of catalog items.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return The newly created item list.
     */
    async createItemList(itemList, options) {
        const config = {
            method: 'post',
            url: '/item-list-ops',
            data: itemList
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Delete all item lists by IDs.
     *
     * @param listIds The IDs of the item lists to delete.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return Results of deleting one or more item lists.
     */
    async deleteItemLists(listIds, options) {
        const config = {
            method: 'delete',
            url: '/item-list-ops/bulk-item-lists',
            params: { listIds }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves the item list by its ID, if it exists.
     *
     * @param listId The item list ID.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return A simple collection of catalog items.
     */
    async getItemList(listId, options) {
        const config = {
            method: 'get',
            url: `/item-list-ops/${listId}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Replaces an item list.
     * @param listId The item list ID.
     * @param itemList A simple collection of catalog items.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return A simple collection of catalog items.
     */
    async replaceItemList(listId, itemList, options) {
        const config = {
            method: 'put',
            url: `/item-list-ops/${listId}`,
            data: itemList
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update an item list.
     * @param listId The item list ID.
     * @param itemList A simple collection of catalog items.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return A simple collection of catalog items.
     */
    async updateItemList(listId, itemList, options) {
        const config = {
            method: 'patch',
            url: `/item-list-ops/${listId}`,
            data: itemList
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update an item list name.
     * @param listId The item list ID.
     * @param name A new name value for the specified item list.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return A simple collection of catalog items.
     */
    async updateItemListName(listId, name, options) {
        const config = {
            method: 'patch',
            url: `/item-list-ops/${listId}/name`,
            data: { name }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update an item list attributes, can remove and add attributes as well.
     * @param listId The item list ID.
     * @param attributes A map of attributes to remove/add/update.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return A simple collection of catalog items.
     */
    async updateItemListAttributes(listId, attributes, options) {
        const config = {
            method: 'patch',
            url: `/item-list-ops/${listId}/attributes`,
            data: { attributes }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Deletes an item list.
     * @param listId The item list ID.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async deleteItemList(listId, options) {
        const config = {
            method: 'delete',
            url: `/item-list-ops/${listId}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Shares an Item List.
     * @param shareItemListRequest Provides info about which item list to share,
     * who it should be shared with, and which access level should be granted.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async createShareItemList(listId, shareItemListRequest, options) {
        const config = {
            method: 'post',
            url: `/item-list-ops/${listId}/share`,
            data: shareItemListRequest
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves a list of item list items.
     * @param listId The item list ID.
     * @param itemIds The item list item IDs.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return The list of item list items on this item list.
     */
    async listSelectItemListItems(listId, itemIds, options) {
        const config = {
            method: 'get',
            url: `/item-list-ops/${listId}/bulk-items`,
            params: { itemIds }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Creates a list of item list items for this item list.
     * @param listId The item list ID.
     * @param listItems A list of item list items to create.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return
     */
    async createItemListItems(listId, listItems, options) {
        const config = {
            method: 'post',
            url: `/item-list-ops/${listId}/bulk-items`,
            data: listItems
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Replaces the list of item list items on this item list.
     * @param listId The item list ID.
     * @param listItems The list of item list items to replace.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return
     */
    async replaceItemListItems(listId, listItems, options) {
        const config = {
            method: 'put',
            url: `/item-list-ops/${listId}/bulk-items`,
            data: listItems
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Updates the list of item list items on this item list.
     * @param listId The item list ID.
     * @param listItems The list of item list items to update.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return
     */
    async updateItemListItems(listId, listItems, options) {
        const config = {
            method: 'patch',
            url: `/item-list-ops/${listId}/bulk-items`,
            data: listItems
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Deletes a list of item list items on this item list.
     * @param listId The item list ID.
     * @param itemIds The item list item IDs.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async deleteItemListItems(listId, itemIds, options) {
        const config = {
            method: 'delete',
            url: `/item-list-ops/${listId}/bulk-items`,
            params: { itemIds }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves a page of item list items from this item list.
     * @param listId The item list ID.
     * @param options passed to the HTTP request call to customize the request configuration. Including paging related options.
     * @return The list of item list items on this item list.
     */
    async listItemListItems(listId, options) {
        const config = {
            method: 'get',
            url: `/item-list-ops/${listId}/items`
        };
        this.attachNumberedPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Creates an item list item on this item list..
     * @param listId The item list ID.
     * @param listItem The item list item to create.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async createItemListItem(listId, listItem, options) {
        const config = {
            method: 'post',
            url: `/item-list-ops/${listId}/items`,
            data: listItem
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves an item list item from this item list
     * @param listId The item list ID.
     * @param itemId The item list item ID.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async getItemListItem(listId, itemId, options) {
        const config = {
            method: 'get',
            url: `/item-list-ops/${listId}/items/${itemId}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Replaces an item list item on this item list.
     * @param listId The item list ID.
     * @param listItem The item list item to replace.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async replaceItemListItem(listId, itemId, listItem, options) {
        const config = {
            method: 'put',
            url: `/item-list-ops/${listId}/items/${itemId}`,
            data: listItem
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Updates an item list item on this item list.
     * @param listId The item list ID.
     * @param listItem The item list item to update.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async updateItemListItem(listId, itemId, listItem, options) {
        const config = {
            method: 'patch',
            url: `/item-list-ops/${listId}/items/${itemId}`,
            data: listItem
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Deletes an item list item on this item list.
     * @param listId The item list ID.
     * @param itemId The item list item ID to delete.
     * @param options passed to the HTTP request call to customize the request configuration.
     * @return
     */
    async deleteItemListItem(listId, itemId, options) {
        const config = {
            method: 'delete',
            url: `/item-list-ops/${listId}/items/${itemId}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Attaches numbered pageable params (page, size, etc.) to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachNumberedPageableParamsIfPresent(config, options) {
        if (options?.page) {
            config.params.offset = options.page;
        }
        if (options?.size) {
            config.params.size = options.size;
        }
    }
}
exports.ItemListClient = ItemListClient;
//# sourceMappingURL=item-list-client.js.map